import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiArrowLeft, FiChevronLeft, FiChevronRight, FiClock } from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';
import { getPriceListPromotionHistory } from '../api';
import InlineLoading from '../components/InlineLoading';
import useDebouncedCallback from '../hooks/useDebouncedCallback';
import useCurrentUser from '../hooks/useCurrentUser';
import useProductEvents from '../hooks/useProductEvents';
import usePromotionClock from '../hooks/usePromotionClock';
import { hasPermission } from '../utils/permissions';
import {
  PRICE_LIST_PROMOTION_STATES,
  formatPromotionDate,
  formatPromotionTerms,
  getPromotionState,
  normalizePriceListPromotion,
} from '../utils/priceListPromotions';

const PAGE_SIZE = 25;

const statePresentation = {
  [PRICE_LIST_PROMOTION_STATES.SCHEDULED]: {
    label: 'Programada',
    classes: 'border-blue-200 bg-blue-50 text-blue-800',
  },
  [PRICE_LIST_PROMOTION_STATES.ACTIVE]: {
    label: 'Activa',
    classes: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  },
  [PRICE_LIST_PROMOTION_STATES.EXPIRED]: {
    label: 'Expirada',
    classes: 'border-slate-200 bg-slate-100 text-slate-700',
  },
  [PRICE_LIST_PROMOTION_STATES.ENDED]: {
    label: 'Finalizada',
    classes: 'border-amber-200 bg-amber-50 text-amber-800',
  },
  [PRICE_LIST_PROMOTION_STATES.CANCELLED]: {
    label: 'Cancelada',
    classes: 'border-red-200 bg-red-50 text-red-700',
  },
  [PRICE_LIST_PROMOTION_STATES.REPLACED]: {
    label: 'Reemplazada',
    classes: 'border-violet-200 bg-violet-50 text-violet-800',
  },
  [PRICE_LIST_PROMOTION_STATES.ARCHIVED]: {
    label: 'Archivada',
    classes: 'border-slate-200 bg-slate-100 text-slate-700',
  },
  [PRICE_LIST_PROMOTION_STATES.IMPORTED]: {
    label: 'Importada',
    classes: 'border-cyan-200 bg-cyan-50 text-cyan-800',
  },
};

const resolveErrorMessage = (error, fallback) => (
  error?.response?.data?.message || error?.message || fallback
);

const formatDateTime = (value) => {
  if (!value) return 'Sin registro';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Sin registro';
  return new Intl.DateTimeFormat('es-CL', {
    timeZone: 'America/Santiago',
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
};

const actorLabel = (actor) => {
  if (!actor) return 'Sistema';
  if (typeof actor === 'string') return actor;
  return actor.name || actor.username || (actor.id ? `Usuario ${actor.id}` : 'Sistema');
};

const targetLabel = (promotion) => {
  const target = promotion.target || {};
  const names = [target.productName, target.varietyName, target.formatName].filter(Boolean);
  if (names.length) return names.join(' · ');
  return promotion.varietyPriceId
    ? `Formato #${promotion.varietyPriceId}`
    : 'Formato histórico';
};

const PromotionHistoryPage = () => {
  const { branchId } = useParams();
  const navigate = useNavigate();
  const { user, status } = useCurrentUser();
  const canRead = hasPermission(user, 'price.read');
  const [promotions, setPromotions] = useState([]);
  const [promotionClock, setPromotionClock] = useState(null);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: PAGE_SIZE,
    total: 0,
    totalPages: 1,
    hasNext: false,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const requestIdRef = useRef(0);

  const loadHistory = useCallback(async ({ silent = false } = {}) => {
    if (!branchId || !canRead) return;
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    if (!silent) setLoading(true);
    setError('');
    try {
      const response = await getPriceListPromotionHistory(branchId, {
        page,
        pageSize: PAGE_SIZE,
      });
      if (requestId !== requestIdRef.current) return;
      const rows = Array.isArray(response?.data) ? response.data : [];
      setPromotions(rows.map(normalizePriceListPromotion));
      setPromotionClock(response?.promotionClock || null);
      setPagination({
        page: Number(response?.page) || page,
        pageSize: Number(response?.pageSize) || PAGE_SIZE,
        total: Number(response?.total) || 0,
        totalPages: Math.max(1, Number(response?.totalPages) || 1),
        hasNext: Boolean(response?.hasNext),
      });
    } catch (loadError) {
      if (requestId !== requestIdRef.current) return;
      setError(resolveErrorMessage(loadError, 'No se pudo cargar el historial de precios temporales.'));
      if (!silent) setPromotions([]);
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [branchId, canRead, page]);

  useEffect(() => {
    requestIdRef.current += 1;
    setPromotions([]);
    setPromotionClock(null);
    setPage(1);
    setLoading(Boolean(branchId && canRead));
  }, [branchId, canRead]);

  useEffect(() => {
    if (status === 'loading' || status === 'idle') return;
    if (!canRead) {
      setLoading(false);
      return;
    }
    void loadHistory();
  }, [canRead, loadHistory, status]);

  const clockPromotions = useMemo(() => (
    promotionClock?.nextBoundaryAt
      ? [...promotions, { startsAt: promotionClock.nextBoundaryAt }]
      : promotions
  ), [promotions, promotionClock?.nextBoundaryAt]);
  const now = usePromotionClock(
    clockPromotions,
    () => void loadHistory({ silent: true }),
    promotionClock
  );
  const { schedule: scheduleHistoryRefresh } = useDebouncedCallback(() => {
    if (canRead) void loadHistory({ silent: true });
  }, 250);
  useProductEvents(canRead ? branchId : null, scheduleHistoryRefresh);

  if (status === 'loading' || status === 'idle' || loading) {
    return (
      <main className="min-h-screen bg-slate-100 px-4 pb-10 pt-20 md:pt-4">
        <div className="mx-auto flex max-w-screen-xl justify-center rounded-2xl bg-white p-10 shadow-sm">
          <InlineLoading label="Cargando historial de precios temporales" />
        </div>
      </main>
    );
  }

  if (!canRead) {
    return (
      <main className="min-h-screen bg-slate-100 px-4 pb-10 pt-20 md:pt-4">
        <div className="mx-auto max-w-xl rounded-2xl border border-amber-200 bg-amber-50 p-6 text-center">
          <h1 className="text-lg font-bold text-amber-950">Sin acceso a precios</h1>
          <p className="mt-2 text-sm text-amber-800">Necesitas el permiso price.read para consultar este historial.</p>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-slate-100 px-2 pb-12 pt-20 sm:px-4 md:pt-4 lg:px-6">
      <div className="mx-auto max-w-screen-xl space-y-4">
        <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
          <button
            type="button"
            onClick={() => navigate(`/branches/${branchId}/price-list`)}
            className="inline-flex min-h-9 items-center gap-2 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-50"
          >
            <FiArrowLeft aria-hidden="true" />
            Volver a la lista de precios
          </button>
          <div className="mt-4 flex items-start gap-3">
            <span className="mt-0.5 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-800">
              <FiClock aria-hidden="true" className="h-5 w-5" />
            </span>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-amber-700">Registro de versiones</p>
              <h1 className="mt-1 text-2xl font-black tracking-tight text-slate-950 sm:text-3xl">Historial de precios temporales</h1>
              <p className="mt-1 text-sm text-slate-600">
                Consulta precios temporales activas, programadas, vencidas, finalizadas y reemplazadas. Este registro es de solo lectura.
              </p>
            </div>
          </div>
        </section>

        {error && (
          <div role="alert" className="flex flex-col gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 sm:flex-row sm:items-center sm:justify-between">
            <span>{error}</span>
            <button type="button" onClick={() => void loadHistory()} className="rounded-lg border border-red-300 px-3 py-1.5 hover:bg-red-100">Reintentar</button>
          </div>
        )}

        {!error && !promotions.length ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-500">
            Todavía no hay precios temporales en el historial.
          </div>
        ) : !error && (
          <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[940px] text-left text-sm">
                <caption className="sr-only">Versiones históricas de precios temporales</caption>
                <thead className="bg-slate-50 text-xs font-bold uppercase tracking-wide text-slate-500">
                  <tr>
                    <th scope="col" className="px-4 py-3">Estado</th>
                    <th scope="col" className="px-4 py-3">Precio temporal</th>
                    <th scope="col" className="px-4 py-3">Producto y formato</th>
                    <th scope="col" className="px-4 py-3">Vigencia</th>
                    <th scope="col" className="px-4 py-3">Registro</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {promotions.map((promotion) => {
                    const state = getPromotionState(promotion, now);
                    const presentation = statePresentation[state] || statePresentation.archived;
                    return (
                      <tr key={promotion.id || `${promotion.varietyPriceId}:${promotion.createdAt}`} className="align-top">
                        <td className="px-4 py-3">
                          <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-bold ${presentation.classes}`}>
                            {presentation.label}
                          </span>
                          {promotion.batchId && (
                            <span className="mt-1 block text-[11px] text-slate-400">Lote {promotion.batchId}</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <strong className="text-slate-950">{formatPromotionTerms(promotion)}</strong>
                          <span className="mt-1 block text-xs text-slate-500">Versión #{promotion.id || 'importada'}</span>
                          {promotion.replacesPromotionId && (
                            <span className="block text-[11px] text-violet-700">Reemplaza la versión #{promotion.replacesPromotionId}</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-semibold text-slate-800">{targetLabel(promotion)}</span>
                          {promotion.target?.barcode && (
                            <span className="mt-1 block text-xs text-slate-500">Código {promotion.target.barcode}</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-slate-700">
                          <span className="block">{formatPromotionDate(promotion.startDate)}</span>
                          <span className="block text-xs text-slate-500">hasta {formatPromotionDate(promotion.endDate)}, inclusive</span>
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-600">
                          <span className="block"><strong>Creada:</strong> {formatDateTime(promotion.createdAt)}</span>
                          <span className="block">por {actorLabel(promotion.createdBy || promotion.creator)}</span>
                          {promotion.endedAt && (
                            <span className="mt-1 block"><strong>Terminada:</strong> {formatDateTime(promotion.endedAt)} por {actorLabel(promotion.endedBy || promotion.ender)}</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex flex-col gap-2 border-t border-slate-100 px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between">
              <p className="text-slate-600">
                Página {pagination.page} de {pagination.totalPages} · {pagination.total} registros
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((current) => Math.max(1, current - 1))}
                  className="inline-flex min-h-9 items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                  <FiChevronLeft aria-hidden="true" /> Anterior
                </button>
                <button
                  type="button"
                  disabled={!pagination.hasNext && page >= pagination.totalPages}
                  onClick={() => setPage((current) => current + 1)}
                  className="inline-flex min-h-9 items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                  Siguiente <FiChevronRight aria-hidden="true" />
                </button>
              </div>
            </div>
          </section>
        )}
      </div>
    </main>
  );
};

export default PromotionHistoryPage;
